// when.ts — turning "sunday" / "the day before yesterday" / "3 days ago" into a date.
//
// WHY THIS EXISTS: the board can now be corrected a week or more back, but until this the chat
// could only ever mark TODAY. That made the assistant the least capable way to use the app — you
// could fix last Tuesday by tapping, but not by saying so. Speaking a correction is the thing a
// grid of squares fundamentally cannot do, so it is where the assistant earns its place.
//
// Resolution is ALWAYS BACKWARDS. "Sunday" said on a Wednesday means the Sunday three days ago,
// never the one coming — you cannot have already done a thing that has not happened, and silently
// marking a future day would be the app inventing a record the customer never made.

const DAY = 86_400_000;
const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

const WEEKDAYS: Record<string, number> = {
  sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2, wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4, friday: 5, fri: 5, saturday: 6, sat: 6,
};

const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  a: 1, an: 1, couple: 2, few: 3,
};

/**
 * Resolve a spoken date phrase to an ISO day, or null if it isn't one.
 *
 * `today` is injected rather than read from the clock so this is testable and so a single
 * conversation resolves every phrase against one consistent "now".
 */
export function resolveWhen(phrase: string | undefined, today: string): string | null {
  if (!phrase) return null;
  const s = phrase.trim().toLowerCase().replace(/[.,!?]+$/, "");
  if (!s) return null;

  // An explicit date, already in shape.
  const explicit = s.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (explicit) return explicit[1] <= today ? explicit[1] : null;

  const base = new Date(today + "T00:00:00");
  const back = (n: number) => iso(new Date(base.getTime() - n * DAY));

  if (/^(today|tonight|just now|this morning|this evening|this afternoon)$/.test(s)) return today;
  if (/^(yesterday|last night)$/.test(s)) return back(1);
  if (/^(the day before yesterday|day before yesterday)$/.test(s)) return back(2);

  // "3 days ago" / "a couple of days ago" / "two days back"
  const ago = s.match(/^(?:about\s+)?(?:an?\s+)?(\d+|[a-z]+)(?:\s+of)?\s+days?\s+(?:ago|back)$/);
  if (ago) {
    const n = /^\d+$/.test(ago[1]) ? Number(ago[1]) : NUMBER_WORDS[ago[1]];
    if (n && n > 0 && n <= 365) return back(n);
  }
  if (/^(a|one)\s+week\s+(ago|back)$/.test(s)) return back(7);

  // A weekday name — always the most recent one that has already happened.
  const wd = s.match(/^(?:on\s+|last\s+|this\s+past\s+|this\s+)?([a-z]+)$/);
  if (wd && wd[1] in WEEKDAYS) {
    const want = WEEKDAYS[wd[1]];
    for (let i = 0; i < 7; i++) {
      const d = new Date(base.getTime() - i * DAY);
      // i === 0 (today) counts: "I walked Monday", said on Monday, means today.
      if (d.getDay() === want) return iso(d);
    }
  }

  return null;
}

/** How to SAY a resolved day back, so the confirmation is readable rather than a bare date. */
export function sayWhen(day: string, today: string): string {
  if (day === today) return "today";
  const base = new Date(today + "T00:00:00");
  const d = new Date(day + "T00:00:00");
  const diff = Math.round((base.getTime() - d.getTime()) / DAY);
  if (diff === 1) return "yesterday";
  const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  if (diff < 7) return names[d.getDay()];
  if (diff < 14) return `last ${names[d.getDay()]}`;
  return day;
}

/**
 * Split a trailing date phrase off a spoken habit reference.
 *
 * "morning walk yesterday" → { name: "morning walk", on: <yesterday> }
 *
 * The local fast path matches "check in X" with a greedy tail, so without this the whole phrase
 * becomes the habit NAME — fuzzy matching still finds the habit, and the mark lands on today. The
 * customer says "yesterday", watches the app confirm, and gets the wrong day silently. Splitting
 * here is what makes the offline path agree with the model path.
 *
 * Longest trailing phrase wins, so "the day before yesterday" beats "yesterday".
 */
export function splitTrailingWhen(term: string, today: string): { name: string; on: string | null } {
  const words = term.trim().split(/\s+/);
  for (let k = Math.min(5, words.length - 1); k >= 1; k--) {
    const tail = words.slice(words.length - k).join(" ");
    const on = resolveWhen(tail, today);
    if (on) return { name: words.slice(0, words.length - k).join(" "), on };
  }
  return { name: term.trim(), on: null };
}
